import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RichTextCommentEditor } from '@debank/content-editor';

import { UserAvatar } from '@/components';
import { useCurrentAddressInfo, useCurrentUserPermission, useTheme } from '@/hooks';
import { EditorSubmitContentData } from '@/components';
import { initEditorDependency } from '@/views/CreateStream/initEditorDependency';
import { wrapModalPromise, WrapModalProps } from '@/utils';
import { PermissionBanner } from '@/views/CreateStream/components/PermissionBanner';

import { QuotedArticle } from '../ArticleContent/QuotedArticle';
import { FeedArticle } from '../../types';
import { useFeedCreate, useArticleRepost } from '../../hooks';
import style from './index.module.scss';

type Props = {
  quoteArticle: FeedArticle;
};

const CreateQuoteModal = ({ quoteArticle, hide, onResolve }: Props & WrapModalProps) => {
  const { t } = useTranslation();
  const [disabled, setDisabled] = useState(false);

  useEffect(() => {
    initEditorDependency();
  }, []);

  const { userInfo } = useCurrentAddressInfo();
  const { isDark } = useTheme();
  const { data: permissionData } = useCurrentUserPermission({ operate_type: 'feed_post' });
  const { mutateAsync, isLoading: isCreating } = useFeedCreate();
  const { mutateAsync: repostArticle, isLoading: isReposting } = useArticleRepost(quoteArticle.id);
  const [imageUid, setImageUid] = useState<string>();

  const handlePostClick = async (data: EditorSubmitContentData) => {
    if (!data.content?.trim() && !data.image_ids?.length) {
      if (!quoteArticle.is_reposted) {
        await repostArticle();
      }
    } else {
      await mutateAsync({
        content: data.content,
        image_ids: data.image_ids,
        entities: data.entities,
        quote_article_id: quoteArticle.id,
        type: 'quote',
      });
    }

    onResolve();
    hide();
  };

  return (
    <>
      <PermissionBanner
        inComment
        permissionData={permissionData}
        currentUserInfo={userInfo}
        setDisabled={setDisabled}
        onClickMint={hide}
      />
      <div className={style.body}>
        <UserAvatar className={style.avatar} size={48} data={userInfo?.desc} />
        <RichTextCommentEditor
          placeholderClassName={style.placeholder}
          placeholder={t('ArticleCreateCommentModal.addComment')}
          onConfirm={handlePostClick}
          autoFocus
          maxLength={300}
          isDarkMode={isDark}
          editorClassName={style.editor}
          postBtnClassName={style.postBtn}
          disabled={disabled || isCreating || isReposting}
          required={false}
          onImageChange={setImageUid}
          bodySlot={
            <QuotedArticle
              article={quoteArticle}
              enableTranslate
              className={style.quotedArticle}
              isDetails
              onlyOriginImg={!imageUid}
              disableDetailNav
            />
          }
        />
      </div>
    </>
  );
};

export const openCreateQuoteModal = wrapModalPromise<Props>(CreateQuoteModal, {
  size: 'large',
});
